import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
    FiShare2,
    FiCopy,
    FiCheck,
    FiZap,
    FiStar,
    FiMail,
    FiShield,
    FiPhone,
    FiUsers,
    FiUserCheck,
    FiGift,
} from "react-icons/fi";
import {
    Container,
    Row,
    Col,
    Card,
    Button,
    Badge,
    OverlayTrigger,
    Tooltip,
    ProgressBar,
} from "react-bootstrap";
import { AiFillCaretDown } from "react-icons/ai";
import { Link, useNavigate } from "react-router-dom";


export default function UserProfile() {


    const { profile } = useSelector((state) => state.auth);

    const navigate = useNavigate();

    const data = profile?.data || {};
    const username = data?.name || "—";
    const email = data?.email || "—";
    const mobile = data?.mobileNumber || "—";
    const referralCode = data?.referralCode || "—";
    const status =
        typeof data?.status === "string"
            ? data.status
            : data?.status
                ? "active"
                : "inactive";
    const isVerified = data?.isVerified ?? false;
    const hasActivePlan = data?.hasActivePlan ?? false;
    const referredByName = data?.referredBy?.name || "—";
    const referredByCode = data?.referredBy?.referralCode || "—";

    const sub = data?.subscription || {};
    const level = sub?.level ?? 0;
    const directRefs = sub?.directReferrals ?? 0;
    const teamSize = sub?.teamSize ?? 0;
    const activeDirectRefs = sub?.activeDirectReferrals ?? 0;
    const activeTeamSize = sub?.activeTeamSize ?? 0;

    const lastLogin = data.lastLogin
        ? new Date(data.lastLogin).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
        })
        : "—";

    const REWARDS = [
        {
            id: 1,
            icon: <FiUsers size={18} />,
            label: "Team Size",
            value: teamSize,
            sub: `${activeTeamSize} active members`,
            bar: Math.min((activeTeamSize / Math.max(teamSize, 1)) * 100, 100) || 8,
            cls: "upc-card-team",
        },
        {
            id: 2,
            icon: <FiUserCheck size={18} />,
            label: "Direct Referrals",
            value: directRefs,
            sub: `${activeDirectRefs} active referrals`,
            bar: Math.min((activeDirectRefs / Math.max(directRefs, 1)) * 100, 100) || 8,
            cls: "upc-card-ref",
        },
        {
            id: 3,
            icon: <FiZap size={18} />,
            label: "Network Level",
            value: `Level ${level}`,
            sub: hasActivePlan ? "Active Plan ✔" : "No Active Plan",
            bar: Math.min(level * 20, 100) || 20,
            cls: "upc-card-lvl",
        },
        {
            id: 4,
            icon: <FiGift size={18} />,
            label: "Referral Code",
            value: referralCode,
            sub: `Referred by ${referredByName}`,
            bar: 80,
            cls: "upc-card-gift",
        },
    ];

    const [copied, setCopied] = useState(false);

    const handleShare = async () => {
        try {
            await navigator.clipboard.writeText(
                `${window.location.origin}/profile/${username}`
            );
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Clipboard error:", err);
        }
    };

    const initials = String(username)
        .split(" ")
        .map((w) => w[0] || "")
        .join("")
        .toUpperCase()
        .slice(0, 2);

    return (
        <>

            <div className="breadcrumb-bar mb-2 mx-1">
                <div className="container-fluid">
                    <div className="d-flex flex-row flex-md-row align-items-md-center justify-content-between gap-2 py-2">

                        <div className="breadcrumb-left d-flex align-items-center flex-wrap">
                            <Link to="/user/home" className="breadcrumb-link active my-2">
                                Home
                            </Link>

                            <span className="breadcrumb-separator mx-2">
                                <i className="bi bi-chevron-right"></i>
                            </span>

                            <span
                                className="breadcrumb-link inactive"
                                onClick={() => navigate("/admin/addproject")}
                            >
                                User profile
                            </span>
                        </div>

                        {/* <button
                            className="add-outlet-btn"
                            onClick={() => navigate("/admin/projects")}
                        >
                            <i className="bi bi-list-ul me-2"></i>
                            Projectlist
                        </button> */}

                    </div>
                </div>
            </div>


            <div className="upc-root">

                <div className="upc-mesh" aria-hidden="true">
                    <span className="upc-blob upc-b1" />
                    <span className="upc-blob upc-b2" />
                    <span className="upc-blob upc-b3" />
                </div>

                <Container className="upc-container">

                    <Card className="upc-section border-0 bg-transparent">

                        <Button
                            variant="light"
                            size="sm"
                            className="upc-edit-btn"
                            aria-label="Edit profile"
                        >
                            <AiFillCaretDown size={15} />
                        </Button>

                        <div className="upc-avatar-wrap">
                            <div className="upc-pulse upc-r1" />
                            <div className="upc-pulse upc-r2" />
                            <div className="upc-pulse upc-r3" />
                            <div className="upc-avatar">
                                <span className="upc-avatar-initials">{initials}</span>
                            </div>
                            <div className="upc-avatar-dot" />
                        </div>

                        <div className="upc-identity">

                            <h1 className="upc-name">{username}</h1>

                            <div className="upc-badge-row">
                                <Badge className="upc-badge upc-badge-status">
                                    <FiShield size={10} style={{ marginRight: 4 }} />
                                    {String(status).charAt(0).toUpperCase() + String(status).slice(1)}
                                </Badge>
                                {isVerified && (
                                    <Badge className="upc-badge upc-badge-verified">✔ Verified</Badge>
                                )}
                                {hasActivePlan && (
                                    <Badge className="upc-badge upc-badge-plan">⚡ Active Plan</Badge>
                                )}
                            </div>

                            <div className="upc-info-grid">

                                <div className="upc-info-item">
                                    <FiMail size={13} className="upc-info-icon" />
                                    <span>{email}</span>
                                </div>

                                <div className="upc-info-item">
                                    <FiPhone size={13} className="upc-info-icon" />
                                    <span>{mobile}</span>
                                </div>

                                <div className="upc-info-item">
                                    <FiGift size={13} className="upc-info-icon" />
                                    <span>Code: <strong>{referralCode}</strong></span>
                                </div>

                                <div className="upc-info-item">
                                    <FiUsers size={13} className="upc-info-icon" />
                                    <span>Referred by: <strong>{referredByName}</strong> ({referredByCode})</span>
                                </div>

                                <div className="upc-info-item upc-info-full">
                                    <FiStar size={13} className="upc-info-icon" />
                                    <span>Last login: {lastLogin}</span>
                                </div>

                            </div>

                        </div>

                        <Row className="upc-stats-strip">

                            {[
                                { n: teamSize, l: "Team Size" },
                                { n: directRefs, l: "Referrals" },
                                { n: `Lvl ${level}`, l: "Network" },

                            ].map((s) => (

                                <Col key={s.l}>

                                    <div className="upc-stat">

                                        <span className="upc-stat-n">{s.n}</span>

                                        <span className="upc-stat-l">{s.l}</span>

                                    </div>
                                </Col>
                            ))}
                        </Row>

                        <div className="upc-share-wrap">
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{copied ? "Link copied!" : "Share Profile"}</Tooltip>}
                            >
                                <Button className="upc-share-btn" onClick={handleShare}>

                                    {copied ? <FiCheck size={14} /> : <FiShare2 size={14} />}

                                    {copied ? "Copied!" : "Share Profile"}

                                    {!copied && <FiCopy size={12} style={{ opacity: 0.5 }} />}

                                </Button>

                            </OverlayTrigger>

                        </div>

                    </Card>

                    <div className="upc-section-label">Earnings &amp; Rewards</div>

                    <Row className="upc-rewards-grid g-3">

                        {REWARDS.map((r) => (

                            <Col md={3} sm={6} xs={12} key={r.id}>

                                <Card className={`upc-reward-card ${r.cls}`}>

                                    <Card.Body>
                                        <div className="upc-rc-top">
                                            <span className="upc-rc-icon">{r.icon}</span>
                                            <span className="upc-rc-label">{r.label}</span>
                                        </div>
                                        <div className="upc-rc-value">{r.value}</div>
                                        <div className="upc-rc-sub">{r.sub}</div>
                                        <ProgressBar now={r.bar} className="upc-rc-bar-wrap" />

                                    </Card.Body>
                                </Card>

                            </Col>

                        ))}

                    </Row>

                </Container>
            </div>
        </>
    );
}